<template>
  <v-container fluid>
    <v-progress-linear
      v-show="resourceLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <div v-if="!resourceLoading && resources.length === 0">
      <div v-if="myFavorites">You have no favorite services</div>
      <div v-else-if="myCollection">You have no ordered services</div>
      <div v-else-if="suggested">You have no suggested services</div>
      <div v-else>No data found for the specified criteria</div>
    </div>
    <div
      v-if="!resourceLoading && resources.length > 0 && showSortBy"
      class="d-flex flex-row mb-8 align-center"
    >
      <div>
        Sort by:
      </div>
      <v-select
        class="ml-2"
        v-model="sortBy"
        :items="[
          { text: 'Default', value: null },
          { text: 'Recently updated', value: 'updatedTimestamp', desc: true },
          { text: 'Recently added', value: 'createdTimestamp', desc: true },
          { text: 'A-Z', value: 'name', desc: false },
        ]"
        dense
        style="max-width: 180px"
        outlined
        rounded
        hide-details
        return-object
        @input="changeSortBy"
      ></v-select>
    </div>

    <v-row class="mb-10 justify-start">
      <v-col
        cols="12"
        xl="4"
        lg="4"
        md="6"
        sm="6"
        xs="12"
        class="pa-3"
        v-for="(resource, index) in resources"
        :key="'resource-' + index"
      >
        <v-hover v-slot="{ hover }">
          <v-card
            class="d-flex flex-column"
            elevation="3"
            style="border-radius: 24px; height: 100%; "
            :class="{ 'on-hover': hover }"
            @click="handleCardClick(resource.id, $event)"
          >
            <div style="position: relative; width: 100%; text-align: center; overflow: hidden;">
              <v-app-bar
                flat
                class="custom-app-bar"
                style="position: absolute; top: 0; left: 0; width: 100%; z-index: 1; background: transparent;"
              >
                <v-chip flat label small color="blue" class="white--text font-weight-bold">
                  Service
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="!resource.isFavorite && $store.getters.roles"
                  @click="addFavorite(resource)"
                  icon
                  raised
                  medium
                >
                  <v-icon>mdi-heart-outline</v-icon>
                </v-btn>
                <v-btn
                  v-if="resource.isFavorite && $store.getters.roles"
                  @click="removeFavorite(resource)"
                  icon
                  raised
                  medium
                  color="red"
                >
                  <v-icon>mdi-heart</v-icon>
                </v-btn>
              </v-app-bar>
              <div style="display: flex; justify-content: center; width: 100%; overflow: hidden;">
                <v-img
                  v-if="resource.picture"
                  height="200px"
                  max-width="100%"
                  v-bind:src="resource.picture"
                  :style="{ borderTopLeftRadius: '24px', borderTopRightRadius: '24px' }"
                ></v-img>
                <v-img
                  v-else-if="resource.logoUrlPublic"
                  height="200px"
                  max-width="80%"
                  contain
                  v-bind:src="resource.logoUrlPublic"
                  :style="{ borderTopLeftRadius: '24px', borderTopRightRadius: '24px' }"
                ></v-img>
                <v-img
                  v-else
                  height="200px"
                  max-width="100%"
                  lazy-src="@/assets/img/blur.jpg"
                  :style="{ borderTopLeftRadius: '24px', borderTopRightRadius: '24px' }"
                ></v-img>
              </div>
            </div>

            <v-card-text>
              <div class="text-start" align="start" justify="start">
                <v-col cols="12" class="black--text">
                  <b style="font-size: 20px;">{{ resource.name }}</b>
                </v-col>
              </div>
              <div class="text-start">
                <v-col cols="12">
                  <label class="primary200--text">Provider</label>
                  <div>
                    <router-link
                      :to="{
                        name: 'OverviewProvider',
                        params: { id: resource.provider.id },
                        query: { serviceQuery: true },
                      }"
                      style="text-decoration: underline; color: inherit;"
                    >
                      {{ resource.provider.name }}
                    </router-link>
                  </div>
                </v-col>
              </div>
              <div class="text-start">
                <v-col cols="12">
                  <label class="primary200--text">Hosting Institution</label>
                  <div>
                    <router-link
                      :to="{
                        name: 'OverviewHostingInstitution',
                        params: { id: resource.provider.hostingInstitution.id },
                        query: { serviceQuery: true },
                      }"
                      style="text-decoration: underline; color: inherit;"
                    >
                      {{ resource.provider.hostingInstitution.name }}
                    </router-link>
                  </div>
                </v-col>
              </div>
              <div class="text-start">
                <v-col cols="12">
                  <label class="primary200--text">Tags</label>
                  <div v-if="resource.tags && resource.tags.length > 0" class="d-flex">
                    <div v-for="(tag, index) in resource.tags" :key="index" class="ma-2">
                      <v-chip small>
                        {{ tag }}
                      </v-chip>
                    </div>
                  </div>
                  <div v-else>
                    -
                  </div>
                </v-col>
              </div>
            </v-card-text>

            <v-spacer></v-spacer>
            <v-divider class="custom-divider"></v-divider>

            <v-card-actions>
              <v-row align="center">
                <v-col class="d-flex align-center">
                  <v-btn @click="redirectToOverview(resource.id)" icon style="min-width: auto;">
                    <v-icon style="color: rgba(0, 0, 0, 0.87)!important;" class="mr-2">
                      mdi-arrow-top-right
                    </v-icon>
                  </v-btn>
                  <p class="mb-0">More Details</p>
                </v-col>
                <v-col
                  v-if="
                    providerId != resource.provider.id && resource.publicContactsVerified.length > 0
                  "
                  class="d-flex justify-end"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        @click.stop=""
                        :href="'mailto:' + resource.publicContactsVerified[0].email"
                        icon
                        v-bind="attrs"
                        v-on="on"
                        style="min-width: auto;"
                      >
                        <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                          mdi-email-outline
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>Contact</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-hover>
      </v-col>
    </v-row>

    <v-row v-if="!myFavorites && !myCollection && resources.length > 0" class="custom-pagination">
      <v-spacer></v-spacer>
      <v-col cols="4" md="2" class="justify-end d-flex flex-row align-center">
        <div>{{ resourcesLength }} Results</div>
      </v-col>
      <v-col cols="8" md="2" class="justify-end d-flex flex-row align-center">
        <div class="pr-2">Show:</div>
        <div style="max-width: 90px">
          <v-select
            v-model="itemsPerPage"
            :items="[5, 10, 15, 25, 50, 100]"
            dense
            outlined
            hide-details
          ></v-select>
        </div>
      </v-col>
      <v-col cols="12" md="4" class="justify-end d-flex flex-row align-center">
        <v-pagination
          v-model="options.page"
          :length="pagesLength"
          :total-visible="7"
        ></v-pagination>
      </v-col>
    </v-row>
    <v-dialog v-model="modalLogIn" max-width="1000px">
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="px-0 py-0">
          <v-row class="mx-0 px-2" align="center">
            <v-col cols="11" class="text-center d-flex justify-center align-center">
              This action requires authentication
            </v-col>
            <v-col cols="1" class="pl-0">
              <v-btn fab small text @click="modalLogIn = false">
                <v-icon color="neutral500">
                  mdi-close
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider class="mt-1 mb-6 my-sm-6 my-lg-6 my-xl-6 my-md-6"></v-divider>
        <v-card-text class="pa-0">
          Only authenticated users are allowed to perform this action. Please
          <router-link :to="{ name: 'MarketplaceService', params: { id: itemSelectedId } }"
            >Log In</router-link
          >
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus';
import base from '@/services/base-service.js';
import util from '@/mixins/util.js';
import service from '@/services/base-service';

export default {
  name: 'MarketplaceServicesComponent',
  mixins: [base, util, service],
  props: {
    myCollection: {
      type: Boolean,
      default: false,
    },
    myFavorites: {
      type: Boolean,
      default: false,
    },
    suggested: {
      type: Boolean,
      default: false,
    },
    showSortBy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      firstLoad: true,
      resources: [],
      resourcesLength: null,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      search: {},
      searchAttributes: [
        'text',
        'providers',
        'domains',
        'subDomains',
        'categories',
        'superCategories',
        'subCategories',
        'isEquipment',
        'accessTypes',
        'accessModes',
        'noCost',
        'institutions',
        'targets',
      ],
      pagesLength: 1,
      itemsPerPage: 10,
      queries: '',
      resourceLoading: true,
      sortBy: null,
      providerId: null,
      roles: null,
      modalLogIn: false,
      institutions: [],
      itemSelectedId: null,
    };
  },
  created() {
    this.providerId = sessionStorage.getItem('providerId');
    this.roles = localStorage.getItem('roles');
    this.getInstitutions().then(res => {
      this.institutions = res.data;
    });
  },
  mounted() {
    /* Populate table from route */
    this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
    this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
    this.options.sortBy = [];
    this.options.sortDesc = [];
    if (this.$route.query.sort) {
      const sortList = this.$route.query.sort.split(',');
      for (let i = 0; i < sortList.length; i += 1) {
        this.options.sortBy.push(sortList[i].split(' ')[0]);
        if (sortList[i].split(' ')[1] === 'asc') {
          this.options.sortDesc.push(false);
        } else {
          this.options.sortDesc.push(true);
        }
      }
    }
    for (let i = 0; i < this.searchAttributes.length; i += 1) {
      const attr = this.searchAttributes[i];
      if (this.$route.query[attr]) {
        this.$set(this.search, attr, this.$route.query[attr]);
      }
    }
    this.itemsPerPage = this.options.itemsPerPage;
    this.sortBy = this.options.sortBy.length > 0 ? this.options.sortBy[0] : null;
    this.getDataFromApi();
    this.queries = this.$route.query.text ? this.$route.query.text : '';
  },
  methods: {
    handleCardClick(serviceId, event) {
      if (event.target.closest('a, button, .v-btn, .v-icon')) {
        return;
      }
      this.redirectToOverview(serviceId);
    },
    redirectToOverview(resourceId) {
      if (this.$route.path === '/app/my-favorites') {
        const favoriteService = true;

        this.$router.push({
          name: 'OverviewService',
          params: { id: resourceId },
          query: { favoriteService },
        });
      }
      if (this.$route.path === '/app/suggested') {
        const suggestedService = true;

        this.$router.push({
          name: 'OverviewService',
          params: { id: resourceId },
          query: { suggestedService },
        });
      }
    },
    getDataFromApi() {
      let urlApi = '/api/v1/resources?';
      if (this.myCollection) {
        urlApi = '/api/v1/orders/my-collection';
      }
      if (this.myFavorites) {
        urlApi = '/api/v1/favorites';
      }
      if (this.suggested) {
        urlApi = '/api/v1/resources/suggested';
      }
      this.resourceLoading = true;
      this.axios
        .get(urlApi, {
          params: this.getParams(),
        })
        .then(res => {
          this.resources = res.data.content;
          this.pagesLength = res.data.pagesLength;
          this.firstLoad = false;
          this.resourcesLength = res.data.total;
          this.$emit('resourcesLength', res.data.total);
        })
        .finally(() => {
          this.resourceLoading = false;
        });
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const query = {
        index: page - 1,
        size: itemsPerPage,
      };
      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }
        sort += `${sortBy[i]} ${dir}`;
      }
      if (sort !== '') {
        query.sort = sort;
      }
      for (let i = 0; i < this.searchAttributes.length; i += 1) {
        const attr = this.searchAttributes[i];
        if (this.search[attr]) {
          query[attr] = this.search[attr];
        }
      }
      return query;
    },
    addFavorite(item) {
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/favorites/' + item.id)
        .then(() => {
          EventBus.$emit('snackbar', {
            text: 'Favorite added successfully',
          });
          item.isFavorite = true;
          this.$emit('add-favorite', true);
        })
        .catch(error => {
          EventBus.$emit('snackbar', {
            text: error.response.data,
            color: 'warning',
          });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    removeFavorite(item, index) {
      EventBus.$emit('waiting', true);
      this.axios
        .delete('/api/v1/favorites/' + item.id)
        .then(() => {
          EventBus.$emit('snackbar', {
            text: 'Favorite removed successfully',
          });
          item.isFavorite = false;
          if (this.myFavorites) {
            console.log(index);
            this.resources.splice(index, 1);
          }
        })
        .catch(error => {
          EventBus.$emit('snackbar', {
            text: error.response.data,
            color: 'warning',
          });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    // changeOptions() {
    //   console.log('change options');
    //   this.$router
    //     .push({
    //       path: this.$router.path,
    //       query: this.getParams(),
    //     })
    //     .catch(err => {
    //       throw new Error(`Problem handling something: ${err}.`);
    //     });
    // },
    changeSortBy(item) {
      console.log('change options: ' + item);
      if (item.value == null) {
        this.options = {
          ...this.options,
          sortBy: [],
          sortDesc: [],
        };
      } else {
        this.options = {
          ...this.options,
          sortBy: [item.value],
          sortDesc: [item.desc],
        };
      }
      this.$router
        .push({
          path: this.$router.path,
          query: this.getParams(),
        })
        .catch(err => {
          throw new Error(`Problem handling something: ${err}.`);
        });
    },
    setTitle() {
      if (this.myFavorites) {
        return 'Favorites';
      } else if (this.myCollection) {
        return '';
      } else if (this.suggested) {
        return 'Suggested';
      } else if (this.countFilters(this.search) > 1) {
        return 'Multiple Filters Applied';
      } else if (this.search.domains && this.search.domains.length > 0) {
        return this.search.domains;
      } else if (this.search.institutions && this.search.institutions.length > 0) {
        let institutions = [];
        const ids = this.search.institutions.split(',');
        for (let id of ids) {
          const inst = this.institutions.find(element => element.id == id);
          if (inst) {
            institutions.push(inst.name);
          }
        }
        return institutions.toString();
      } else if (this.search.categories && this.search.categories.length > 0) {
        return this.search.categories;
      } else if (this.search.superCategories && this.search.superCategories.length > 0) {
        return this.search.superCategories;
      }
    },
    onEditItem(value) {
      console.log('Click Edit ' + value.id);
      this.$router.push({
        name: 'AdminServicesEdit',
        query: { id: value.id },
      });
    },
    onContactItem(value) {
      console.log('Contact ' + value.id);
      this.itemSelectedId = value.id;
      this.modalLogIn = true;
    },
  },
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.getDataFromApi();
        }
      },
      deep: true,
    },
    itemsPerPage(value) {
      if (!this.firstLoad) {
        this.options = {
          ...this.options,
          itemsPerPage: value,
          page: 1,
        };
      }
    },
  },
};
</script>

<style scoped>
.resource-card {
  cursor: pointer;
  box-shadow: 0.869231px 3.47692px 37.3769px -7.82308px rgb(80 67 199 / 20%) !important;
  border-radius: 30px !important;
  border: unset !important;
}

.resource-card:hover {
  box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%),
    0px 1px 10px 0px rgb(0 0 0 / 12%) !important;
}

.resource-image {
  border-radius: 19.875px;
}

.custom-divider {
  padding: 5px 0;
  margin: 0 35px;
  border-top-width: 2px;
  border-top-style: solid;
  border-top-color: rgba(195, 194, 199, 1);
}
</style>
